import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import { useFamily } from '../../../context/FamilyContext';
import MyButton from '../../basic/MyButton';
import MyTextInput from '../../basic/MyTextInput';
import MyToggle from '../../basic/MyToggle';
import MyLoading from '../../basic/MyLoading';
import MyCustomIcon from '../../basic/MyCustomIcon';
import { HelpCircle } from 'lucide-react';
import { fetchFamilyMemberDetails, updateFamilyMemberDetails, checkEmailExistence, checkUsernameExistence } from '../../../api';
import { debounce } from 'lodash';
import './ModalEditFamilyMember.css';

Modal.setAppElement('#root');

function ModalEditFamilyMember({ isOpen, onClose, memberId, onEditSuccess }) {
    const { refetchFamilyData } = useFamily();
    const [memberDetails, setMemberDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isLoginDetailsChangeActive, setIsLoginDetailsChangeActive] = useState(false);
    const [formData, setFormData] = useState({
        display_name: '',
        email: '',
        allow_splits_edit: false,
        email_opt_in: false,
        username: '',
        password: '********',
        confirmPassword: '',
    });
    const [initialFormData, setInitialFormData] = useState({});
    const [isPasswordChanged, setIsPasswordChanged] = useState(false);
    const [passwordsMatch, setPasswordsMatch] = useState(true);

    const [emailExists, setEmailExists] = useState(false);
    const [usernameExists, setUsernameExists] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const [emailMessage, setEmailMessage] = useState('');
    const [usernameMessage, setUsernameMessage] = useState('');

    useEffect(() => {
        if (isOpen && memberId) {
            setIsLoading(true);
            fetchFamilyMemberDetails(memberId)
                .then(data => {
                    setMemberDetails(data);
                    const newFormData = {
                        display_name: data.display_name,
                        email: data.email || '',
                        allow_splits_edit: data.allow_splits_edit,
                        email_opt_in: data.email_opt_in,
                        username: data.username,
                        password: '********',
                        confirmPassword: '',
                    };
                    setFormData(newFormData);
                    setInitialFormData(newFormData);
                    setError('');
                    setIsPasswordChanged(false);
                    setPasswordsMatch(true);
                    setIsLoginDetailsChangeActive(false);
                    setEmailExists(false);
                    setUsernameExists(false);
                    setEmailMessage('');
                    setUsernameMessage('');
                })
                .catch(err => {
                    console.error('Error fetching member details:', err);
                    setError('Failed to load member details.');
                })
                .finally(() => setIsLoading(false));
        }
    }, [isOpen, memberId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'password' && value !== '********') {
            setIsPasswordChanged(true);
        }
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'password' || name === 'confirmPassword') {
            const otherField = name === 'password' ? 'confirmPassword' : 'password';
            setPasswordsMatch(value === formData[otherField]);
        }

        if (name === 'email') {
            sequentialEmailCheck(value);
        } else if (name === 'username') {
            sequentialUsernameCheck(value);
        }
    };

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const MIN_USERNAME_LENGTH = 4;

    const sequentialEmailCheck = useCallback(
        debounce(async (email) => {
            setIsCheckingEmail(false);
            setEmailExists(false);
            setEmailMessage('');
    
            if (email.trim() === '') {
                return;
            }
    
            if (!EMAIL_REGEX.test(email)) {
                setEmailMessage('Please enter a valid email address');
                setEmailExists(true);
                return;
            }
    
            if (email === initialFormData.email) {
                return;
            }
    
            setIsCheckingEmail(true);
            setEmailMessage('Checking email...');
            
            try {
                const { exists } = await checkEmailExistence(email);
                setEmailExists(exists);
                setEmailMessage(exists 
                    ? 'This Email is already registered, try another email address.'
                    : 'This Email is available.');
            } catch (error) {
                console.error('Error checking email existence:', error);
                setEmailMessage('Error checking email. Please try again.');
                setEmailExists(true);
            } finally {
                setIsCheckingEmail(false);
            }
        }, 300),
        [initialFormData.email]
    );

    const sequentialUsernameCheck = useCallback(
        debounce(async (username) => {
            // Check for spaces first
            if (username.includes(' ')) {
                setUsernameMessage('Usernames cannot have spaces');
                setUsernameExists(true);
                return;
            }
    
            if (username.length >= MIN_USERNAME_LENGTH) {
                if (username !== initialFormData.username) {
                    setIsCheckingUsername(true);
                    setUsernameMessage('Checking username...');
                    try {
                        const response = await checkUsernameExistence(username);
                        if (response && typeof response.exists === 'boolean') {
                            setUsernameExists(response.exists);
                            setUsernameMessage(response.exists
                                ? 'This username is taken, create a unique username.'
                                : 'Username is available.');
                        } else {
                            console.error('Unexpected response format:', response);
                            setUsernameMessage('Error checking username. Please try again.');
                            setUsernameExists(true);
                        }
                    } catch (error) {
                        console.error('Error checking username existence:', error);
                        setUsernameMessage('Error checking username. Please try again.');
                        setUsernameExists(true);
                    } finally {
                        setIsCheckingUsername(false);
                    }
                }
            } else {
                setUsernameMessage(`Username must be at least ${MIN_USERNAME_LENGTH} characters long.`);
                setUsernameExists(true);
            }
        }, 300),
        [initialFormData.username]
    );

    const handleToggleChange = (name) => {
        setFormData(prev => ({ ...prev, [name]: !prev[name] }));
    };

    const isValidEmail = (email) => {
        return email === '' || EMAIL_REGEX.test(email);
    };

    const isFormChanged = () => {
        return JSON.stringify(formData) !== JSON.stringify(initialFormData) ||
               (isLoginDetailsChangeActive && isPasswordChanged);
    };

    const canSaveChanges = () => {
        if (!isFormChanged()) return false;
        if (isPasswordChanged && !passwordsMatch) return false;
        if (!isValidEmail(formData.email)) return false;
        if (emailExists || usernameExists) return false;
        if (isCheckingEmail || isCheckingUsername) return false;
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSaveChanges()) return;

        const updateData = {
            display_name: formData.display_name,
            email: formData.email,
            allow_splits_edit: formData.allow_splits_edit,
            email_opt_in: formData.email_opt_in,
        };

        if (isLoginDetailsChangeActive) {
            updateData.username = formData.username;
            if (isPasswordChanged) {
                updateData.password = formData.password;
            }
        }

        try {
            await updateFamilyMemberDetails(memberId, updateData);
            await refetchFamilyData();
            onEditSuccess('Family member updated successfully!');
            handleClose();
        } catch (err) {
            console.error('Error updating family member:', err);
            setError('Failed to update family member.');
        }
    };

    const resetLoginDetails = () => {
        setFormData(prev => ({
            ...prev,
            username: initialFormData.username,
            password: '********',
            confirmPassword: '',
        }));
        setIsPasswordChanged(false);
        setPasswordsMatch(true);
        setUsernameExists(false);
        setUsernameMessage('');
    };

    const handleClose = () => {
        setIsLoginDetailsChangeActive(false);
        onClose();
    };

    const tooltip_email = `<p><strong>PiggyBunker emails,</strong>  include weekly financial updates, and the occasional new feature highlight.</p>`;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Edit Family Member"
            className="edit-family-member-modal"
            overlayClassName="edit-family-member-modal-overlay"
        >
            <h2>Edit Family Member</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmit}>
                <MyTextInput
                    label="Display Name"
                    name="display_name"
                    value={formData.display_name}
                    onChange={handleInputChange}
                    required
                />
                <div className="input-group">
                    <MyTextInput
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                    />
                    {isCheckingEmail && (
                        <div className="indicator-wrapper">
                            <MyLoading size="small" />
                        </div>
                    )}
                    {emailMessage && !isCheckingEmail && (
                        <div className="indicator-wrapper">
                            <div className="indicator-content">
                                {emailExists ? (
                                    <span className="error-message">{emailMessage}</span>
                                ) : (
                                    <>
                                        <MyCustomIcon type="check" color="green" />
                                        <span className="success-text">{emailMessage}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <div className="toggle-with-tooltip">
                <MyToggle
                    label={
                        <span className="label-with-icon">
                            Receive emails from PiggyBunker
                            <span className="tooltip-container">
                                <HelpCircle size={18} className="tooltip-icon" color="#4CAF50" />
                                <span className="tooltip-text" dangerouslySetInnerHTML={{ __html: tooltip_email }}></span>
                            </span>
                        </span>
                    }
                    checked={formData.email_opt_in}
                    onChange={() => handleToggleChange('email_opt_in')}
                    disabled={!isValidEmail(formData.email) || formData.email === ''}
                />
                </div>
                    
                {memberDetails?.role === 'child' && (
                    <MyToggle
                        label="Allow edit income splits"
                        checked={formData.allow_splits_edit}
                        onChange={() => handleToggleChange('allow_splits_edit')}
                    />
                )}
                <div className="login-details-change-section">
                    <MyButton
                        text={isLoginDetailsChangeActive ? "Cancel Login Details Change" : "Change Login Details"}
                        onClick={() => {
                            setIsLoginDetailsChangeActive(!isLoginDetailsChangeActive);
                            if (isLoginDetailsChangeActive) {
                                resetLoginDetails();
                            }
                        }}
                        type="button"
                        backgroundColor={isLoginDetailsChangeActive ? "white" : undefined}
                        color={isLoginDetailsChangeActive ? "#dc3545" : undefined}
                        hoverBackgroundColor={isLoginDetailsChangeActive ? "#f8f8f8" : undefined}
                        hoverColor={isLoginDetailsChangeActive ? "#c82333" : undefined}
                        disabled={isLoading}
                    />
                    {isLoginDetailsChangeActive && (
                        <>
                            <div className="input-group">
                                <MyTextInput
                                    label="Username (must be at least 4 characters long)"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    required
                                />
                                {isCheckingUsername && (
                                    <div className="indicator-wrapper">
                                        <MyLoading size="small" />
                                    </div>
                                )}
                                {usernameMessage && !isCheckingUsername && (
                                    <div className="indicator-wrapper">
                                        <div className="indicator-content">
                                            {usernameExists ? (
                                                <span className="error-message">{usernameMessage}</span>
                                            ) : (
                                                <>
                                                    <MyCustomIcon type="check" color="green" />
                                                    <span className="success-text">{usernameMessage}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <MyTextInput
                                label="New Password"
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleInputChange}
                            />
                            {isPasswordChanged && (
                                <>
                                    <MyTextInput
                                        label="Confirm New Password"
                                        name="confirmPassword"
                                        type="password"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                    />
                    {!passwordsMatch && (
                        <div className="error-message">Passwords do not match</div>
                    )}
                </>
            )}
        </>
    )}
</div>
<div className="modal-actions">
    <MyButton 
        text="Save Changes" 
        type="submit" 
        disabled={isLoading || !canSaveChanges()}
    />
    <MyButton 
        text="Cancel" 
        onClick={handleClose}
        type="button"
        backgroundColor="white"
        color="#dc3545"
        hoverBackgroundColor="#f8f8f8"
        hoverColor="#c82333"
        disabled={isLoading}
    />
</div>
</form>
</Modal>
);
}

export default ModalEditFamilyMember;